import {
  Limit,
  ProgressGauge,
  Status,
  TierType
} from '@com-delta-idp/mnx-capability-flyout-ui';
import { ProgressGaugeOptions } from '@com-delta-omnichannel/mach-core-js';
import {
  LoyaltyMemberTierLevel,
  LoyaltyTier,
  MedallionStatus,
  Threshold
} from '../../models/status-tracker/api/api-status-tracker.interfaces';
import { TYPE } from '../../models/status-tracker/status-tracker.constants';
import { Tracker } from '../../models/status-tracker/status-tracker.interfaces';

export const getEarnometer = (
  mqdThreshold: Threshold | undefined,
  aem: Tracker,
  pending: number,
  tier: TierType
): ProgressGauge => {
  return {
    limits: getLimit(mqdThreshold as Threshold),
    value: mqdThreshold
      ? `${TYPE.CURRENCY_SYMBOL}${decimalSeparator(
          mqdThreshold?.balanceAmt as number
        ).toString()}`
      : `${TYPE.CURRENCY_SYMBOL}${TYPE.MIN_LIMIT}`,
    currency: aem.mqdLabel,
    tier,
    status: getStatus(
      mqdThreshold as Threshold,
      getPending(pending, mqdThreshold)
    ),
    options: getProgressGaugeOptions(
      aem.mqdLabel,
      tier,
      mqdThreshold as Threshold,
      pending
    )
  };
};

const getLimit = (mqdThreshold: Threshold): Limit => {
  return {
    min: `${TYPE.CURRENCY_SYMBOL}${TYPE.MIN_LIMIT}`,
    max: mqdThreshold
      ? `${TYPE.CURRENCY_SYMBOL}${(
          mqdThreshold?.thresholdAmt / 1000
        ).toString()}${TYPE.THOUSAND}`
      : `${TYPE.CURRENCY_SYMBOL}${TYPE.MIN_LIMIT}`
  };
};

const getStatus = (mqdThreshold: Threshold, pending: number): Status => {
  return mqdThreshold
    ? {
        current: mqdThreshold.percentCompleteNum.split('.')[0],
        pending: ((pending / mqdThreshold?.thresholdAmt) * 100).toString()
      }
    : {
        current: TYPE.MIN_LIMIT,
        pending: TYPE.MIN_LIMIT
      };
};

const getPending = (
  pending: number,
  mqdThreshold: Threshold | undefined
): number => {
  return mqdThreshold && mqdThreshold.balanceAmt <= mqdThreshold.thresholdAmt
    ? mqdThreshold.balanceAmt + pending > mqdThreshold.thresholdAmt
      ? mqdThreshold.thresholdAmt - mqdThreshold.balanceAmt
      : pending
    : 0;
};

export const getThreshold = (
  medallionStatus: MedallionStatus[] | undefined,
  type: string
): Threshold | undefined => {
  const thresholds = medallionStatus?.find(
    (status) => status.loyaltyMemberThresholds
  )?.loyaltyMemberThresholds as Threshold[];
  return thresholds?.find((threshold) => threshold.thresholdTypeCode === type);
};

export const getYear = (date: string): number => {
  const dateValue = new Date(date + TYPE.TIME_ZONE);
  return !isNaN(dateValue.getDate()) ? dateValue.getFullYear() : 0;
};

export const getTierName = (tierList: LoyaltyMemberTierLevel[]): TierType => {
  // Get the list of CURRENT tier
  const currentTier = tierList.filter(
    (tier) =>
      tier.tierStatusDesc === TYPE.CURRENT &&
      tier.tierLevelClassificatinCode !== TYPE.MILLION_MILER
  );

  // Check if CURRENT tier has 360
  const is360 = currentTier.find(
    (tier) => tier.tierLevelClassificatinCode === TYPE.DELTA_360_CODE
  );

  return is360
    ? (is360.tierLevelCodeDesc.split(' ')[0].toLowerCase() as TierType)
    : ((
        currentTier.find(
          (tier) => tier.tierLevelClassificatinCode === TYPE.TIER_CODE
        )?.tierLevelCodeDesc || ''
      )
        .split(' ')[0]
        .toLowerCase() as TierType);
};

export const decimalSeparator = (value: number): string => {
  const usFormatter = new Intl.NumberFormat(TYPE.LOCALE);
  return usFormatter.format(value);
};

export const getProgressGaugeOptions = (
  mqdLabel: string,
  tier: TierType,
  mqdThreshold: Threshold,
  pending: number
): ProgressGaugeOptions => {
  return {
    id: `status-tracker-${tier}`,
    currency: TYPE.CURRENCY_SYMBOL,
    earnedType: mqdLabel,
    loyaltyTier: transformTierType(tier),
    gauge: {
      earned: mqdThreshold ? mqdThreshold.balanceAmt : 0,
      pending: pending,
      target: mqdThreshold ? mqdThreshold.thresholdAmt : 0
    }
  };
};

export const transformTierType = (tier: TierType): LoyaltyTier => {
  return tier === 'skymiles' ? 'main' : tier;
};
